import { type GridColDef, GridActionsCellItem, type GridSortModel } from '@mui/x-data-grid-pro';
import { Box, Link, Chip, Typography, Tooltip } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { MtComStyleObj } from '../styles/MtComStyleObj';
import type { Theme } from '@mui/material/styles';
import { toTitleCase, formatDMY } from "../utils/HelperFunction";
import { GenericConstant } from './GenericConstant';
import { createRenderColumnHeader } from '../components/common/GridHeaderUtils';
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import { applyDashValueFormatter, formatMissingText } from './missingValue';

const actionItemSx = (theme: Theme) => ({
    ...MtComStyleObj(theme).gridIconHoverStyle,
    boxShadow: 'none',
});

export const getStatusStyle = (theme: Theme, SPLotStatus: any) => {
    let statusColor = SPLotStatus?.toLowerCase()?.split(" ")?.join("-");
    let statusStyle: any = {
        backgroundColor: GenericConstant.STATUSCOLOR[statusColor],
        color: "#364153",
        ...MtComStyleObj(theme).labelNoto14500
    }

    return statusStyle;
}

const getPriorityStyle = (theme: Theme, priority: any) => {
    let priorityStyle: any = {
        "high": {
            backgroundColor: '#F9A2A6',
            ...MtComStyleObj(theme).labelNoto14500,
            fontWeight: "bold"
        },
        "medium": {
            backgroundColor: '#F7E392',
            ...MtComStyleObj(theme).labelNoto14500,
            fontWeight: "bold"
        },
        "low": {
            backgroundColor: '#85D895',
            ...MtComStyleObj(theme).labelNoto14500,
        }

    }
    return priorityStyle[priority]
}

const renderMissingDash = (classes: any) => (
    <Typography className={classes.labelNoto14Bold400} sx={{ fontSize: 16, fontWeight: 600 }}>—</Typography>
);

const renderTextCell = (classes: any, value: any, className = classes.labelNoto14Bold400) => {
    const displayValue = formatMissingText(value);
    return (
        <Typography className={className} sx={displayValue === '—' ? { fontSize: 16, fontWeight: 600 } : undefined}>
            {displayValue}
        </Typography>
    );
};

export function getSPLotGridCols(setDrilDownData: any, classes: any, downloadCurRow: any, sortModel: GridSortModel = [], printSPLotLabel?: (params: any) => void, showArchivedSPLots = false): GridColDef[] {
    const renderHeader = createRenderColumnHeader(sortModel);

    const columns: GridColDef[] = [
        {
            field: 'splot_id', headerName: 'SP Lot ID', minWidth: 254, renderHeader,
            renderCell: (params: any) => {
                return (
                    <Box
                        sx={{
                            cursor: 'pointer',

                        }}>
                        <Link className={classes.labelNoto14Bold400} onClick={(event) => {
                            event.stopPropagation();
                            setDrilDownData(params);
                        }} sx={{
                            color: "#094BC7",
                            textDecoration: "underline",
                            "&:hover, &:visited": {
                                color: "#094BC7",
                                textDecoration: "underline"
                            }
                        }}>
                            {formatMissingText(params?.row?.splot_id)}
                        </Link>
                    </Box>
                );
            },
        },
        {
            field: 'cropName', headerName: 'Crop', width: 120, cellClassName: classes.labelNoto14Bold400, renderHeader,
            renderCell: (params: any) => renderTextCell(classes, params?.value),
        },
        {
            field: 'status',
            headerName: 'Status',
            minWidth: 160,
            renderHeader,
            renderCell: (params: any) => {
                return (
                    params?.value ? <Chip className={classes.labelNoto14Bold400} label={toTitleCase(params?.value ?? "")} sx={(theme: Theme,) => ({
                        ...getStatusStyle(theme, params?.value?.toLowerCase()),
                        borderRadius: '8px',

                    })} /> : renderMissingDash(classes)
                )
            }
        },
        {
            field: 'priority',
            headerName: 'Priority',
            width: 115,
            renderHeader,
            renderCell: (params: any) => {
                return (
                    params?.value ? <Chip className={classes.labelNoto14Bold400} label={toTitleCase(params?.value ?? "")} sx={(theme: Theme) => ({
                        ...getPriorityStyle(theme, params?.value?.toLowerCase())
                    })} /> : renderMissingDash(classes)
                )
            }
        },
        {
            field: 'trial_id', headerName: 'Trial ID', flex: 1, minWidth: 100, cellClassName: classes.labelNoto14Bold400, renderHeader,
            renderCell: (params: any) => renderTextCell(classes, params?.value),
        },
        {
            field: 'sector', headerName: 'Sector', minWidth: 130, cellClassName: classes.labelNoto14Bold400, renderHeader,
            renderCell: (params: any) => renderTextCell(classes, params?.value),
        },

        {
            field: 'updated_date',
            headerName: 'Last Updated On',
            width: 190,
            cellClassName: classes.labelNoto14Bold400,
            renderHeader,
            renderCell: (params: any) => renderTextCell(classes, formatDMY(params?.value ?? "")),
            valueFormatter: (value: any) => {
                return formatMissingText(formatDMY(value ?? ""))
            }
        },

        {
            field: 'comments',
            headerName: 'Comments',
            width: 250,
            cellClassName: classes.labelNoto14Bold400,
            renderHeader,
            renderCell: (params: any) => renderTextCell(classes, params?.value),
        },
        {
            field: 'Action',
            headerName: 'Actions',
            width: 120,
            type: "actions",
            sortable:false,
            getActions: (params) => [
                ...(!showArchivedSPLots ? [
                    <Tooltip key="print" title="Print SPLot Label" arrow placement="top">
                        <GridActionsCellItem
                            icon={<PrintOutlinedIcon />}
                            label="Print Label"
                            color="inherit"
                            showInMenu={false}
                            sx={actionItemSx}
                            onClick={(event) => {
                                event.stopPropagation();
                                printSPLotLabel?.(params);
                            }}
                        />
                    </Tooltip>,
                ] : []),
                <Tooltip key="download" title="Download SPLot details" arrow placement="top">
                    <GridActionsCellItem
                        icon={<FileDownloadOutlinedIcon />}
                        label="Download"
                        className="textPrimary"
                        color="inherit"
                        sx={actionItemSx}
                        onClick={(event) => {
                            event.stopPropagation();
                            downloadCurRow(params);
                        }}
                    />
                </Tooltip>,
                // <GridActionsCellItem
                //     key="delete"
                //     icon={<DeleteIcon sx={{ color: red }} />}
                //     label="Delete"
                //     color="inherit"

                //     onClick={(event) => {
                //         event.stopPropagation();
                //         //deleteUserConfirm({"user_email":params?.row?.user_email ?? "","deleted_by":globalState?.userId});
                //     }}

                // />
            ]
        }

    ];

    return applyDashValueFormatter(columns);
}
export function editSPLotGridCols(edidSPLoTMatData: any, classes: any, roleSelected: string, showArchivedSPLots: boolean, sortModel: GridSortModel = [], moveToSPLot?: (params: any) => void): GridColDef[] {
    const renderHeader = createRenderColumnHeader(sortModel);

    const columns: GridColDef[] = [
        {
            field: 'material_id', headerName: 'Material ID', flex: 1, minWidth: 150, cellClassName: classes.labelNoto16Bold400, renderHeader,
            renderCell: (params: any) => renderTextCell(classes, params?.value, classes.labelNoto16Bold400),
        },
        {
            field: 'trial_id', headerName: 'Trial ID', flex: 1, minWidth: 160, cellClassName: classes.labelNoto14Bold400, renderHeader,
            renderCell: (params: any) => renderTextCell(classes, params?.value),
        },
        {
            field: 'pollination_id', headerName: 'Pollination ID', flex: 1, minWidth: 150, cellClassName: classes.labelNoto14Bold400, renderHeader,
            renderCell: (params: any) => renderTextCell(classes, params?.value),
        },
        {
            field: 'bags', headerName: 'Bags', flex: 1, minWidth: 90, cellClassName: classes.labelNoto14Bold400, renderHeader,
            renderCell: (params: any) => renderTextCell(classes, params?.value),
        },
        {
            field: 'fruit_weight_g', headerName: 'Fruit Weight (g)', flex: 1, minWidth: 170, cellClassName: classes.labelNoto14Bold400, renderHeader,
            renderCell: (params: any) => renderTextCell(classes, params?.value),
        },
        {
            field: 'fruit_count', headerName: 'Fruit Count', flex: 1, minWidth: 160, cellClassName: classes.labelNoto14Bold400, renderHeader,
            renderCell: (params: any) => renderTextCell(classes, params?.value),
        },
        {
            field: 'containers', headerName: 'Registered Container', flex: 1, minWidth: 160, cellClassName: classes.labelNoto14Bold400, renderHeader,
            renderCell: (params: any) => {
                if (params && params.value && Array.isArray(params.value) && params.value.length > 0) {
                    return (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, width: '100%', justifyContent: 'center', height: '100%' }}>
                            {params.value.map((container: { container_alt_id: string; }, index: number) => (
                                <Typography
                                    key={index}
                                    variant="body2"
                                    sx={{
                                        color: 'black',
                                        wordWrap: 'break-word',
                                        wordBreak: 'break-all',
                                        whiteSpace: 'normal'
                                    }}
                                >
                                    {container.container_alt_id}
                                </Typography>
                            ))}
                        </Box>
                    );
                }

                return renderMissingDash(classes);
            },
        },

        {
            field: 'material_status', headerName: 'Status', flex: 1, minWidth: 160, cellClassName: classes.labelNoto14Bold400, renderHeader,
            renderCell: (params: any) => renderTextCell(classes, params?.value),
        },



        {
            field: 'priority', headerName: 'Priority', flex: 1, minWidth: 140, renderHeader,
            renderCell: (params: any) => {
                return (
                    params?.value ? <Chip className={classes.labelNoto15Bold500} label={toTitleCase(params?.value ?? "")} sx={(theme: Theme) => ({
                        ...getPriorityStyle(theme, params?.value?.toLowerCase())
                    })} /> : renderMissingDash(classes)
                )
            }
        },


        {
            field: 'updated_date', headerName: 'Last Updated On', flex: 1, minWidth: 180, cellClassName: classes.labelNoto14Bold400, renderHeader,
            renderCell: (params: any) => renderTextCell(classes, formatDMY(params?.value ?? "")),
            valueFormatter: (value: any) => {

                return formatMissingText(formatDMY(value ?? ""))
            }
        },

        {
            field: 'remarks', headerName: 'Remarks', flex: 1, minWidth: 130, cellClassName: classes.labelNoto14Bold400, renderHeader,
            renderCell: (params: any) => renderTextCell(classes, params?.value),
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 120,
            type: 'actions',
            getActions: (params) => {
                const actions = [];
                
                actions.push(
                    <Tooltip key="move" title="Move material to another splot" arrow placement="top">
                        <GridActionsCellItem
                            icon={<ExitToAppIcon sx={{ fontSize: 20 }} />}
                            label="Move to SP Lot"
                            color="inherit"
                            disableRipple
                            disableFocusRipple
                            sx={actionItemSx}
                            onClick={(event) => {
                                event.stopPropagation();
                                moveToSPLot?.(params);
                            }}
                        />
                    </Tooltip>
                );
                
                if (!['Breeder', 'Technician'].includes(roleSelected) && !showArchivedSPLots && params?.row?.material_status?.toLowerCase() != "registered") {
                    actions.push(
                        <GridActionsCellItem
                            key="edit"
                            icon={<img
                                src={"icons/Edit.svg"}
                                alt="Save"
                                width={20}
                                height={20}
                                style={{ display: "block" }}
                            />}
                            label="Edit"
                            color="inherit"
                            disableRipple
                            disableFocusRipple
                            sx={actionItemSx}

                            onClick={(event) => {
                                event.stopPropagation();
                                edidSPLoTMatData(params);
                            }}
                        />
                    );
                }

                return actions;
            },
        }

    ];

    return applyDashValueFormatter(columns);
}

